import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useParams } from 'react-router-dom';
import { FaCalendar, FaUser } from 'react-icons/fa';
import { RiArrowLeftLine, RiArrowRightUpLine } from 'react-icons/ri';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import { getBlogPostById, getRelatedPosts, detailImageUrl } from '../../data/blogPosts';
import LanguageContext from '../../context/LanguageContext.js';
import '../Blog/Blog.css';

const BlogDetails = () => {
    const { dir } = useContext(LanguageContext);
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language;
    const { id } = useParams();

    const meta = getBlogPostById(id);
    if (!meta) {
        return <Navigate to={`/${currentLanguage}/blog`} replace />;
    }

    const postId = meta.id;
    const baseKey = `blog.post${postId}`;

    const title = t(`${baseKey}.title`);
    const author = t(`blog.authors.${meta.authorRole}`);
    const categoryLabel = t(`blog.categories.${meta.category}`);
    const key = (suffix) => `${baseKey}.${suffix}`;
    const has = (suffix) => i18n.exists(key(suffix));

    const related = getRelatedPosts(postId, 2);
    const layoutDir = dir();

    return (
        <main className="main">
            <Breadcrumb title={title} />

            <section className="blog-detail-root">
                <div className="container blog-detail-head" dir={layoutDir}>
                    <Link to={`/${currentLanguage}/blog`} className="blog-detail-back-crumb">
                        {t('nav.blog')}
                    </Link>
                    <h1 className="blog-detail-page-title">{title}</h1>
                    <div className="blog-detail-meta-bar">
                        <span className="blog-detail-cat-pill">{categoryLabel}</span>
                        <span className="blog-detail-meta-item">
                            <FaCalendar className="blog-post-meta-ico" aria-hidden />
                            {new Date(meta.date).toLocaleDateString(currentLanguage)}
                        </span>
                        <span className="blog-detail-meta-item">
                            <FaUser className="blog-post-meta-ico" aria-hidden />
                            {author}
                        </span>
                    </div>
                </div>

                <div className="container blog-detail-layout" dir={layoutDir}>
                    <article className="blog-detail-article">
                        <div className="blog-detail-cover-wrap">
                            <img src={detailImageUrl(postId, 1200)} alt={title} className="blog-detail-cover" />
                        </div>
                        <div className="blog-detail-body">
                            {has('fullContent') && <p className="blog-detail-p">{t(key('fullContent'))}</p>}
                            {has('paragraph1') && <p className="blog-detail-p">{t(key('paragraph1'))}</p>}
                            <h2 className="blog-detail-subheading">{t('blog.subheading')}</h2>
                            {has('paragraph2') && <p className="blog-detail-p">{t(key('paragraph2'))}</p>}
                            <blockquote className="blog-detail-pullquote">&ldquo;{t('blog.quote')}&rdquo;</blockquote>
                            {has('paragraph3') && <p className="blog-detail-p">{t(key('paragraph3'))}</p>}
                        </div>
                        <div className="blog-detail-footer">
                            <Link to={`/${currentLanguage}/blog`} className="blog-detail-back-btn">
                                <RiArrowLeftLine className="blog-detail-back-ico" aria-hidden />
                                {t('blog.backToBlog')}
                            </Link>
                        </div>
                    </article>

                    <aside className="blog-detail-aside">
                        <div className="blog-aside-inner blog-aside-inner--narrow">
                            <div className="blog-aside-block">
                                <div className="blog-aside-heading">
                                    <span className="blog-aside-heading-text">{t('blog.relatedPosts')}</span>
                                </div>
                                <ul className="blog-recent-list">
                                    {related.map((rp) => {
                                        const relatedTitle = t(`blog.post${rp.id}.title`);
                                        return (
                                            <li key={rp.id}>
                                                <Link to={`/${currentLanguage}/blog/${rp.id}`} className="blog-recent-link">
                                                    <img src={rp.image} alt="" className="blog-recent-thumb" />
                                                    <div className="blog-recent-text">
                                                        <span className="blog-recent-title">{relatedTitle}</span>
                                                        <span className="blog-recent-cta">
                                                            {t('blog.readMore')}
                                                            <RiArrowRightUpLine className="blog-recent-cta-ico" aria-hidden />
                                                        </span>
                                                    </div>
                                                </Link>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        </div>
                    </aside>
                </div>
            </section>
        </main>
    );
};

export default BlogDetails;
